<template>
  <section
    id="curriculum-panel"
    role="region"
    aria-labelledby="curriculum-heading"
    class="panel panel-default"
  >
    <div class="panel-body">
      <header class="panel-header">
        <h2 id="curriculum-heading">Curriculum</h2>
        <p v-if="overallProgrammeDeadline" class="programme-deadline">
          Curriculum deadline: {{ formatDate(overallProgrammeDeadline) }}
        </p>
      </header>
      <div class="curriculum-modules">
        <curriculum-module-list-section
          title="In Progress"
          :modules="inProgressModules"
          variant="in-progress"
          :compact-limit="2"
          item-style="featured"
        ></curriculum-module-list-section>

        <curriculum-module-list-section
          title="Upcoming"
          :modules="upcomingModules"
          variant="upcoming"
          :compact-limit="2"
        ></curriculum-module-list-section>

        <curriculum-module-list-section
          title="Completed"
          :modules="completedModules"
          variant="completed"
          :compact-limit="3"
          :collapsible="true"
          :initially-expanded="false"
          empty-text="No completed modules yet."
        ></curriculum-module-list-section>
      </div>
    </div>
  </section>
</template>

<style scoped>
header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.curriculum-modules {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.programme-deadline {
  color: var(--kate-type-alt);
  font-size: 0.95rem;
}
</style>

<script>
import { genericSort } from '../../utils/sort-by-object-property';
import TimeMixin from '../../mixins/time-mixins';
import CurriculumModuleListSection from './curriculum_panel/curriculum-module-list-section.vue';

export default {
  components: {
    CurriculumModuleListSection,
  },

  mixins: [TimeMixin],

  props: {
    learnerInfo: {
      type: Object,
      required: true,
    },
    programmeId: {
      type: Number,
      required: true,
    },
    programmeDetails: {
      type: Object,
      default: () => ({}),
    },
    programmeModules: {
      type: Array,
      default: () => [],
      required: true,
    },
  },

  computed: {
    curriculumModules() {
      return this.programmeModules.filter(mod => !this.isMilestoneModule(mod));
    },

    inProgressModules() {
      const now = new Date();
      return this.curriculumModules
        .filter(mod => this.isInProgressModule(mod, now))
        .slice()
        .sort(this.sortByExpectedCompletionDate);
    },

    upcomingModules() {
      const now = new Date();
      return this.curriculumModules
        .filter(mod => this.isUpcomingModule(mod, now))
        .slice()
        .sort(this.sortByReleaseDate);
    },

    completedModules() {
      const now = new Date();
      return this.curriculumModules
        .filter(mod => this.isCompletedModule(mod, now))
        .slice()
        .sort(this.sortByExpectedCompletionDate)
        .reverse();
    },

    overallProgrammeDeadline() {
      const programmeDeadline = this.toValidDate(this.programmeDetails.planned_end_date);
      if (programmeDeadline) {
        return programmeDeadline;
      }
      const expectedDates = this.curriculumModules
        .map(mod => this.toValidDate(mod.expected_completion_date))
        .filter(Boolean);
      if (!expectedDates.length) {
        return undefined;
      }
      return expectedDates.sort((a, b) => b.getTime() - a.getTime())[0];
    },
  },

  methods: {
    isMilestoneModule(mod) {
      if (!mod) {
        return false;
      }
      return mod.track === 'milestone';
    },

    sortModule(a, b) {
      if (a.number && b.number) {
        return genericSort(a.number, b.number);
      }
      if (!a.number && !b.number) {
        return genericSort(a.name, b.name);
      }
      return a.number ? -1 : 1;
    },

    toValidDate(value) {
      if (!value) {
        return undefined;
      }
      const parsed = this.getDate(value);
      if (Number.isNaN(parsed.getTime())) {
        return undefined;
      }
      return parsed;
    },

    sortByExpectedCompletionDate(a, b) {
      const dateA = this.toValidDate(a.expected_completion_date);
      const dateB = this.toValidDate(b.expected_completion_date);

      if (dateA && !dateB) {
        return -1;
      }
      if (!dateA && dateB) {
        return 1;
      }
      if (dateA && dateB) {
        const byDate = genericSort(dateA.getTime(), dateB.getTime());
        if (byDate !== 0) {
          return byDate;
        }
      }

      return this.sortModule(a, b);
    },

    sortByReleaseDate(a, b) {
      const dateA = this.toValidDate(a.release_date);
      const dateB = this.toValidDate(b.release_date);
      if (dateA && dateB) {
        const byDate = genericSort(dateA.getTime(), dateB.getTime());
        if (byDate !== 0) {
          return byDate;
        }
      }
      return this.sortByExpectedCompletionDate(a, b);
    },

    isInProgressModule(mod, now) {
      const releaseDate = this.toValidDate(mod.release_date);
      const expectedCompletionDate = this.toValidDate(mod.expected_completion_date);

      if (!releaseDate || !expectedCompletionDate) {
        return false;
      }

      return releaseDate <= now && mod.percentage < 100;
    },

    isUpcomingModule(mod, now) {
      const releaseDate = this.toValidDate(mod.release_date);
      const expectedCompletionDate = this.toValidDate(mod.expected_completion_date);

      if (!releaseDate || !expectedCompletionDate) {
        return false;
      }

      return releaseDate > now && mod.percentage < 100;
    },

    isCompletedModule(mod, now) {
      const expectedCompletionDate = this.toValidDate(mod.expected_completion_date);
      if (!expectedCompletionDate) {
        return false;
      }

      // Requirement: only completed if 100% and expected date has passed.
      return mod.percentage >= 100 && expectedCompletionDate < now;
    },

    formatDate(value) {
      if (!value) {
        return '';
      }
      return this.parseTimestamp(value, undefined, 'MMM d, yyyy');
    },
  },
};
</script>
